import { MouseEvent, useState, Fragment } from 'react';

import {
  DevHubCard,
  DevHubCheckbox,
  DevHubChip,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { MyApp } from './types/myAppTypes';
import ToggleStar from './ToggleStar';
import { statusColor } from '../../util/statusColor';
import AppEntityIcon from '../common/AppEntityIcon';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

interface Props {
  app: MyApp;
  link: string;
  onStarChange: (appId: number, isStarred: boolean) => void;
  isEditable?: boolean;
  checked?: boolean;
  changeSelect: (app: MyApp) => void;
  disabled?: boolean;
}

/**
 * This is a component that displays a list of apps
 * @param props the app and appId to display
 */
export const AppList = (props: Props) => {
  const borderColor = statusColor(props?.app?.status);

  const getDescription = () => {
    if (props?.app?.description?.length <= 350) {
      return props?.app?.description;
    }
    return `${props?.app?.description.substring(0, 350)}...`;
  };

  const [checked, setChecked] = useState(false);

  const alertApp = (app: MyApp, e: MouseEvent) => {
    e.stopPropagation();
    setChecked(!checked);
    props.changeSelect(app);
  };

  return (
    <DevHubCard
      density="tiny"
      title={props?.app?.name}
      actionItem={
        <Fragment>
          {props.isEditable &&
            (props?.app?.role === 'Owner' ||
              props?.app?.role === 'iTRC Approver' ||
              props?.app?.role === 'Owner Assistant') && (
              <DevHubCheckbox
                key="select-all"
                value="select-all"
                name="select-all"
                onClick={e => alertApp(props.app, e)}
                checked={props.checked}
                color="primary"
                disabled={props.disabled && !props.checked}
              />
            )}
          <ToggleStar app={props?.app} onStarChange={props.onStarChange} />
        </Fragment>
      }
      topBorder="custom"
      topBorderColor={borderColor}
      consistentHeight
      multiColumn
      link={props?.app?.url}
      bodyPosition="bottom"
      body={
        <Fragment>
          <DevHubTypography variant="body2" gutterBottom opacity="hazy">
            {`ID: ${props?.app?.applicationId}`}
            &nbsp;&nbsp;&nbsp;
            {`Status: ${props?.app?.status}`}
          </DevHubTypography>
          {props?.app?.role && (
            <DevHubTypography variant="body2" gutterBottom opacity="hazy">
              {`Assigned Role: ${props.app.role}`}
            </DevHubTypography>
          )}
          <DevHubTypography variant="body2" gutterBottom opacity="hazy">
            {`Owner: ${props?.app?.owner?.displayName}`}
          </DevHubTypography>
          {/* 9.7 — systemType badge */}
          {props?.app?.systemType && (
            <DevHubChip
              size="small"
              label={props.app.systemType === 'AiSystem' ? 'AI System' : 'App'}
              color={
                props.app.systemType === 'AiSystem' ? 'primary' : 'default'
              }
            />
          )}
          {/* 9.8 — Has AI badge */}
          {ConfigHelper.instance.isFeatureEnabled(Feature.AI_INVENTORY) &&
            props?.app?.systemType !== 'AiSystem' &&
            (props?.app?.aiUseCaseIds?.length ?? 0) > 0 && (
              <DevHubChip size="small" label="Has AI" />
            )}
        </Fragment>
      }
      bodyMiddle={
        <DevHubTypography variant="body1">{getDescription()}</DevHubTypography>
      }
      bottomRight={
        <DevHubGrid container alignItems="center">
          <DevHubGrid item>
            <AppEntityIcon entity={props?.app?.corporateEntity} size="tiny" />
          </DevHubGrid>
        </DevHubGrid>
      }
    />
  );
};
